import { create } from 'zustand';
import { supabase } from '@/integrations/supabase/client';
import type { CloudService, CloudServiceInsert, CloudServiceUpdate } from '@/types/cloudService';
import { toast } from 'sonner';

interface CloudServiceState {
  cloudServices: CloudService[];
  loading: boolean;
  loadCloudServices: () => Promise<void>;
  addCloudService: (service: CloudServiceInsert) => Promise<CloudService | null>;
  updateCloudService: (id: string, updates: CloudServiceUpdate) => Promise<void>;
  deleteCloudService: (id: string) => Promise<void>;
  getCloudServiceById: (id: string) => CloudService | undefined;
}

export const useCloudServiceStore = create<CloudServiceState>((set, get) => ({
  cloudServices: [],
  loading: false,

  loadCloudServices: async () => {
    set({ loading: true });
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: profile } = await supabase
        .from('profiles')
        .select('org_id')
        .eq('user_id', user.id)
        .single();

      if (!profile?.org_id) throw new Error('No organization found');

      const { data, error } = await supabase
        .from('cloud_services')
        .select('*')
        .eq('org_id', profile.org_id)
        .order('name');

      if (error) throw error;

      console.log('✅ Cloud services loaded:', data?.length || 0, 'services', data);
      set({ cloudServices: (data || []) as CloudService[], loading: false });
    } catch (error) {
      console.error('❌ Error loading cloud services:', error);
      toast.error('Failed to load cloud services');
      set({ loading: false });
    }
  },

  addCloudService: async (service: CloudServiceInsert) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: profile } = await supabase
        .from('profiles')
        .select('org_id')
        .eq('user_id', user.id)
        .single();

      if (!profile?.org_id) throw new Error('No organization found');

      const { data, error } = await supabase
        .from('cloud_services')
        .insert({
          ...service,
          org_id: profile.org_id,
          owner_id: service.owner_id || null,
        })
        .select()
        .single();

      if (error) throw error;

      set((state) => ({
        cloudServices: [...state.cloudServices, data as CloudService],
      }));

      toast.success('Cloud service added successfully');
      return data as CloudService;
    } catch (error) {
      console.error('Error adding cloud service:', error);
      toast.error('Failed to add cloud service');
      return null;
    }
  },

  updateCloudService: async (id: string, updates: CloudServiceUpdate) => {
    try {
      const updateData = {
        ...updates,
        owner_id: updates.owner_id === undefined ? undefined : (updates.owner_id || null),
      };
      const { error } = await supabase
        .from('cloud_services')
        .update(updateData)
        .eq('id', id);

      if (error) throw error;

      set((state) => ({
        cloudServices: state.cloudServices.map((service) =>
          service.id === id ? { ...service, ...updates } : service
        ),
      }));

      toast.success('Cloud service updated successfully');
    } catch (error) {
      console.error('Error updating cloud service:', error);
      toast.error('Failed to update cloud service');
    }
  },

  deleteCloudService: async (id: string) => {
    try {
      const { error } = await supabase
        .from('cloud_services')
        .delete()
        .eq('id', id);

      if (error) throw error;

      set((state) => ({
        cloudServices: state.cloudServices.filter((service) => service.id !== id),
      }));

      toast.success('Cloud service deleted successfully');
    } catch (error) {
      console.error('Error deleting cloud service:', error);
      toast.error('Failed to delete cloud service');
    }
  },

  getCloudServiceById: (id: string) => {
    return get().cloudServices.find((service) => service.id === id);
  },
}));
